/**
 * Validation for supplier quote PDF extraction review (required + soft warnings).
 */

import {
  getQuoteSupplierFormat,
  isSupportedQuoteSupplierFormat,
} from '../config/quoteSupplierFormats';
import {
  hasValidQuoteCreditFunding,
  QUOTE_FUNDING_REQUIRED_MESSAGE,
} from './quoteFundingPayload';

/** @type {string} */
export const ENCO_QUOTE_FORMAT_ID = 'enco_scientific_quote_pdf';

/** @type {string} */
export const RHENIUM_QUOTE_FORMAT_ID = 'rhenium';

/**
 * @param {import('../types/quoteExtractionTypes').ExtractedQuoteData} data
 * @returns {string|null}
 */
function resolveFormatId(data) {
  const raw = data.meta?.supplierFormat ?? data.meta?.parserFormat;
  if (raw == null || String(raw).trim() === '') return null;
  const key = String(raw).trim().toLowerCase();
  if (key === 'rhenium_quote_pdf') return RHENIUM_QUOTE_FORMAT_ID;
  return key;
}

/**
 * @param {import('../types/quoteExtractionTypes').ExtractedQuoteData} data
 * @returns {boolean}
 */
export function isRheniumQuoteFormat(data) {
  return resolveFormatId(data) === RHENIUM_QUOTE_FORMAT_ID;
}

/**
 * @param {import('../types/quoteExtractionTypes').ExtractedQuoteData} data
 * @returns {boolean}
 */
export function isEncoQuoteFormat(data) {
  return resolveFormatId(data) === ENCO_QUOTE_FORMAT_ID;
}

/**
 * @param {*} value
 * @returns {boolean}
 */
function isBlank(value) {
  return value == null || String(value).trim() === '';
}

/**
 * @param {*} value
 * @returns {number|null}
 */
function toNumber(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * @param {Array} items
 * @returns {number|null}
 */
function sumLineTotals(items) {
  let sum = 0;
  let hasAny = false;
  for (const item of items || []) {
    const lineTotal = toNumber(item.lineTotal);
    if (lineTotal != null) {
      sum += lineTotal;
      hasAny = true;
    }
  }
  return hasAny ? Math.round(sum * 100) / 100 : null;
}

/**
 * @typedef {Object} ValidationResult
 * @property {boolean} canSave
 * @property {string[]} blockingErrors
 * @property {import('../types/quoteExtractionTypes').ExtractionWarning[]} warnings
 */

/**
 * @param {import('../types/quoteExtractionTypes').ExtractedQuoteData} data
 * @param {{ supplierId?: string|number|null, supplierOptions?: Array, budget?: string, fundingCreditId?: string|number|null }} [context]
 * @returns {ValidationResult}
 */
export function validateExtractedQuoteForSave(data, context = {}) {
  const blockingErrors = [];
  const warnings = [...(data.warnings || [])];

  const formatId = resolveFormatId(data);
  const isEnco = formatId === ENCO_QUOTE_FORMAT_ID;
  const isRhenium = isRheniumQuoteFormat(data);
  if (formatId && !isSupportedQuoteSupplierFormat(formatId)) {
    blockingErrors.push(
      `Quote format “${formatId}” is not supported. Re-extract or contact an administrator.`
    );
  }

  const supplierId = context.supplierId;
  if (supplierId == null || String(supplierId).trim() === '') {
    blockingErrors.push('Supplier is required before saving.');
  }

  const hasBudgetLine =
    context.budgetLineId != null && String(context.budgetLineId).trim() !== '';
  const budgetText = String(context.budget ?? '').trim();
  const hasBudget = budgetText !== '';
  const hasFundingCredit = String(context.fundingCreditId ?? '').trim() !== '';
  if (!hasBudgetLine && !hasBudget && !hasFundingCredit) {
    blockingErrors.push(QUOTE_FUNDING_REQUIRED_MESSAGE);
  } else if (!hasBudgetLine && !hasBudget && hasFundingCredit) {
    const fundableItems = (data.items || []).map(item => ({
      quantity: item.quantity,
      price: String(item.unitPriceAfterDiscount ?? item.unitPrice ?? ''),
      discount:
        item.discountPercent != null ? String(item.discountPercent) : '',
    }));
    if (
      !hasValidQuoteCreditFunding({
        fundingCreditId: context.fundingCreditId,
        items: fundableItems,
      })
    ) {
      blockingErrors.push(
        'Supplier credit requires line items with quantity and price.'
      );
    }
  }

  if (isEnco && isBlank(data.supplier?.name)) {
    blockingErrors.push('Supplier name is required.');
  }

  const formatDef = formatId ? getQuoteSupplierFormat(formatId) : null;
  if (
    formatDef &&
    !isRhenium &&
    supplierId != null &&
    String(supplierId).trim() !== '' &&
    Array.isArray(context.supplierOptions)
  ) {
    const rematch = formatDef.matchMateriahSupplierOption(
      context.supplierOptions,
      data.supplier
    );
    if (
      rematch &&
      String(rematch.value) !== String(supplierId) &&
      rematch.score >= 55
    ) {
      warnings.push({
        field: 'supplier',
        message: `Selected supplier does not match the ${formatDef.displayName} name on the PDF. Expected a name like “${rematch.label}”.`,
        severity: 'warning',
      });
    }
  }
  const items = data.items || [];
  const quoteTotal = isEnco
    ? toNumber(data.quote?.subtotal)
    : toNumber(data.quote?.total) ??
      (isRhenium ? sumLineTotals(items) : null);

  if (isBlank(data.quote?.quoteNumber)) {
    blockingErrors.push('Quote number is required.');
  }
  if (!isRhenium && isBlank(data.quote?.quoteDate)) {
    blockingErrors.push('Quote date is required.');
  }
  if (quoteTotal == null) {
    blockingErrors.push('Quote total is required.');
  }
  if (isEnco && isBlank(data.quote?.currency)) {
    blockingErrors.push('Currency is required.');
  }

  if (items.length === 0) {
    blockingErrors.push(
      'No line items were detected. Add at least one item before saving.'
    );
  }

  items.forEach((item, index) => {
    const row = index + 1;
    const hasCatalog =
      !isBlank(item.catalogNumber) || !isBlank(item.productNumber);
    const hasDesc = !isBlank(item.description);

    if (isEnco) {
      if (!hasCatalog) {
        blockingErrors.push(`Row ${row}: product/catalog number is required.`);
      }
      if (!hasDesc) {
        blockingErrors.push(`Row ${row}: description is required.`);
      }
      if (item.quantity == null || toNumber(item.quantity) == null) {
        blockingErrors.push(`Row ${row}: quantity is required.`);
      }
      if (toNumber(item.unitPrice) == null) {
        blockingErrors.push(`Row ${row}: unit price is required.`);
      }
    } else {
      if (!hasCatalog && !hasDesc) {
        blockingErrors.push(`Row ${row}: catalog number or description is required.`);
      }
      if (item.quantity == null || toNumber(item.quantity) == null) {
        blockingErrors.push(`Row ${row}: quantity is required.`);
      }
      const hasPrice =
        toNumber(item.unitPriceAfterDiscount) != null ||
        toNumber(item.unitPrice) != null;
      const hasLineTotal = toNumber(item.lineTotal) != null;
      if (!hasPrice && !hasLineTotal) {
        blockingErrors.push(`Row ${row}: unit price or line total is required.`);
      }
    }
  });

  if (!isRhenium && isBlank(data.quote?.vatAmount)) {
    warnings.push({
      field: 'quote.vatAmount',
      message: 'VAT amount was not detected.',
      severity: 'warning',
    });
  }
  if (!isRhenium && isBlank(data.quote?.paymentTerms)) {
    warnings.push({
      field: 'quote.paymentTerms',
      message: 'Payment terms are missing.',
      severity: 'warning',
    });
  }
  if (!isRhenium && isBlank(data.quote?.validUntil)) {
    warnings.push({
      field: 'quote.validUntil',
      message: 'Valid-until date is missing.',
      severity: 'warning',
    });
  }

  items.forEach((item, index) => {
    const row = index + 1;
    if (!isEnco && isBlank(item.catalogNumber)) {
      warnings.push({
        field: `items[${index}].catalogNumber`,
        message: `Row ${row}: catalog number is missing.`,
        severity: 'warning',
      });
    }
    if (isBlank(item.unitPriceCurrency) && isBlank(item.lineTotalCurrency)) {
      warnings.push({
        field: `items[${index}].currency`,
        message: `Row ${row}: item currency is missing.`,
        severity: 'warning',
      });
    }

    if (isEnco) {
      if (isBlank(item.unitPriceCurrency)) {
        warnings.push({
          field: `items[${index}].currency`,
          message: `Row ${row}: unit price currency is missing.`,
          severity: 'warning',
        });
      }
      if (isBlank(item.casNumber)) {
        warnings.push({
          field: `items[${index}].casNumber`,
          message: `Row ${row}: CAS number is missing.`,
          severity: 'info',
        });
      }
      if (isBlank(item.productDataSheetUrl)) {
        warnings.push({
          field: `items[${index}].productDataSheetUrl`,
          message: `Row ${row}: product datasheet URL is missing.`,
          severity: 'info',
        });
      }
    }
  });

  const subtotal = toNumber(data.quote?.subtotal);
  const vat = toNumber(data.quote?.vatAmount);
  const total = toNumber(data.quote?.total);
  const vatRate = toNumber(data.quote?.vatRate);
  if (!isRhenium && !isEnco && subtotal != null && vat != null && total != null) {
    const expected = Math.round((subtotal + vat) * 100) / 100;
    const actual = Math.round(total * 100) / 100;
    if (Math.abs(expected - actual) > 0.05) {
      warnings.push({
        field: 'quote.total',
        message: 'Quote total does not match subtotal + VAT.',
        severity: 'warning',
      });
    }
  }
  if (isEnco && subtotal != null && vatRate != null && vat != null) {
    const expectedVat = Math.round(subtotal * (vatRate / 100) * 100) / 100;
    if (Math.abs(expectedVat - vat) > 0.05) {
      warnings.push({
        field: 'quote.vatAmount',
        message: 'VAT amount does not match subtotal × VAT rate.',
        severity: 'warning',
      });
    }
  }

  return {
    canSave: blockingErrors.length === 0,
    blockingErrors,
    warnings,
  };
}
